import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLoader, FiCheckCircle, FiAlertCircle } = FiIcons;

export default function ProgressView({ progress, logs, error, onReset }) {
  const logsEndRef = useRef(null);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="w-full max-w-3xl mx-auto bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden"
    >
      <div className="p-10">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-6">
            {error ? (
              <div className="bg-red-100 p-4 rounded-2xl">
                <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-600" />
              </div>
            ) : progress === 100 ? (
              <div className="bg-green-100 p-4 rounded-2xl">
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-600" />
              </div>
            ) : (
              <div className="bg-brand-light p-4 rounded-2xl">
                <SafeIcon icon={FiLoader} className="w-8 h-8 text-brand-accent animate-spin" />
              </div>
            )}
            <div>
              <h3 className="text-xl font-bold text-brand-dark">
                {error ? 'Processing Failed' : progress === 100 ? 'Processing Complete' : 'Preparing Print Pack...'}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {error ? 'An error occurred during processing.' : 'Generating physical references and tab mapping.'}
              </p>
            </div>
          </div>
          <div className="text-3xl font-black text-brand-accent/30">
            {Math.round(progress)}%
          </div>
        </div>

        <div className="w-full bg-slate-100 rounded-full h-3.5 mb-10 overflow-hidden">
          <motion.div
            className={`h-3.5 rounded-full ${error ? 'bg-red-500' : 'bg-brand-accent'}`}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <div className="bg-brand-dark rounded-2xl p-6 h-80 overflow-y-auto font-mono text-xs shadow-inner border border-white/10">
          {logs.map((log, index) => (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              key={index}
              className={`mb-2.5 flex items-start gap-3 ${
                log.startsWith('ERROR') ? 'text-red-400' : 
                log.startsWith('Success') ? 'text-brand-accent' : 'text-slate-300'
              }`}
            >
              <span className="text-slate-600 shrink-0">[{new Date().toLocaleTimeString()}]</span>
              <span className="leading-relaxed">{log}</span>
            </motion.div>
          ))}
          <div ref={logsEndRef} />
        </div>

        {(progress === 100 || error) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 flex justify-end"
          >
            <button
              onClick={onReset}
              className="px-8 py-3 bg-brand-dark hover:bg-brand-dark/90 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-brand-dark/20"
            >
              Process Another File
            </button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}